import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChatCircleDots, MagnifyingGlass, Eye, ArrowsClockwise, CheckCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export default function SentViberPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await api.get('/viber-logs', { params });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Greska pri ucitavanju Viber loga');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const showDetail = async (id) => {
    try {
      const res = await api.get(`/viber-logs/${id}`);
      setDetail(res.data);
    } catch {
      toast.error('Greska pri ucitavanju detalja');
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleString('sr-Latn-ME', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  const StatusBadge = ({ status, delivery_status }) => {
    if (status === 'failed') {
      return <Badge className="bg-red-100 text-red-700 border-red-300"><XCircle size={12} className="mr-1" />Greška</Badge>;
    }
    if (delivery_status === 'DELIVERED') {
      return <Badge className="bg-green-100 text-green-700 border-green-300"><CheckCircle size={12} className="mr-1" />Isporučeno</Badge>;
    }
    if (delivery_status === 'PENDING') {
      return <Badge className="bg-blue-100 text-blue-700 border-blue-300"><CheckCircle size={12} className="mr-1" />U toku</Badge>;
    }
    return <Badge className="bg-purple-100 text-purple-700 border-purple-300"><CheckCircle size={12} className="mr-1" />Poslano</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="sent-viber-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ChatCircleDots size={26} weight="duotone" className="text-purple-600" />Poslate Viber poruke</h1>
          <p className="text-sm text-slate-500">Evidencija svih Viber poruka poslatih iz CRM-a — preko Infobip API-ja.</p>
        </div>
        <Button onClick={load} variant="outline" data-testid="viber-refresh-btn"><ArrowsClockwise size={16} className="mr-2" />Osvjezi</Button>
      </div>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[250px]">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
                placeholder="Pretraga po broju ili poruci..." className="pl-10 bg-white border-slate-300" data-testid="viber-search-input" />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setTimeout(load, 50); }}>
              <SelectTrigger className="w-48 bg-white border-slate-300" data-testid="viber-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Svi statusi</SelectItem>
                <SelectItem value="sent">Poslano</SelectItem>
                <SelectItem value="failed">Greška</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={load} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="viber-filter-btn">Filtriraj</Button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Datum</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Telefon</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Poruka</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-slate-600 uppercase text-xs">Akcije</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Ucitavanje...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Nema poslatih Viber poruka</td></tr>
                ) : items.map(it => (
                  <tr key={it.id} className="hover:bg-slate-50" data-testid={`viber-row-${it.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-slate-700 font-mono text-xs">{fmtDate(it.sent_at)}</td>
                    <td className="px-3 py-2 text-slate-900 font-medium">{it.to_phone}</td>
                    <td className="px-3 py-2 text-slate-700 max-w-md truncate" title={it.text}>{it.text}</td>
                    <td className="px-3 py-2"><StatusBadge status={it.status} delivery_status={it.delivery_status} /></td>
                    <td className="px-3 py-2">
                      <Button size="sm" variant="ghost" onClick={() => showDetail(it.id)} data-testid={`viber-view-${it.id}`}>
                        <Eye size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-slate-500">Ukupno: <strong>{total}</strong> {loading ? '' : `(prikazano ${items.length})`}</div>
        </CardContent>
      </Card>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalji Viber poruke</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><strong>Datum:</strong> {fmtDate(detail.sent_at)}</div>
                <div><strong>Status:</strong> <StatusBadge status={detail.status} delivery_status={detail.delivery_status} /></div>
                <div><strong>Telefon:</strong> {detail.to_phone}</div>
                <div><strong>Message ID:</strong> <span className="font-mono text-xs">{detail.message_id || '-'}</span></div>
                {detail.delivery_status && <div className="col-span-2"><strong>Delivery status:</strong> {detail.delivery_status}</div>}
                {detail.error && <div className="col-span-2 text-red-700"><strong>Greška:</strong> {detail.error}</div>}
              </div>
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 whitespace-pre-wrap text-sm">
                {detail.text || ''}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
